import React, { useState } from 'react';
import { ChatBubbleOutline as ChatIcon } from '@mui/icons-material';

interface ChatWidgetProps {
  className?: string;
}

const SOPHIE_AVATAR =
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=96&auto=format&fit=crop';

const ChatWidget: React.FC<ChatWidgetProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');

  const handleSendMessage = () => {
    if (message.trim()) {
      // Ici vous pouvez ajouter la logique pour envoyer le message
      // TODO: Implémenter l'envoi de message
      setMessage('');
      // Optionnel: fermer le chat après envoi
      // setIsOpen(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Bouton chat flottant */}
      <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-neutral-200/80 bg-white text-neutral-800 shadow-soft-md transition-all hover:bg-neutral-50 hover:shadow-soft-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/15 focus-visible:ring-offset-2"
          aria-label="Discuter"
          title="Discuter"
          aria-expanded={isOpen}
        >
          <ChatIcon className="h-[22px] w-[22px]" aria-hidden />
        </button>
      </div>

      {/* Panneau de chat */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-50 w-80 bg-white rounded-lg shadow-soft-lg border border-neutral-200 overflow-hidden">
          {/* En-tête du chat */}
          <div className="bg-primary-500 text-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img
                  src={SOPHIE_AVATAR}
                  alt="Sophie"
                  width={40}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="w-10 h-10 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <h3 className="font-semibold text-sm">Discuter avec Sophie</h3>
                  <p className="text-white/90 text-xs">En ligne • Répond généralement en quelques minutes</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-white/80 transition-colors flex-shrink-0"
                aria-label="Fermer le chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Zone de messages */}
          <div className="h-64 p-4 overflow-y-auto bg-neutral-50">
            <div className="space-y-3">
              {/* Message de bienvenue */}
              <div className="flex items-start gap-2">
                <img
                  src={SOPHIE_AVATAR}
                  alt="Sophie"
                  width={32}
                  height={32}
                  loading="lazy"
                  decoding="async"
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="bg-white rounded-lg p-3 shadow-soft text-sm">
                  <p className="text-neutral-700">
                    Bonjour ! 👋 Comment puis-je vous aider aujourd'hui ?
                  </p>
                  <span className="text-xs text-neutral-500 mt-1 block">Sophie • maintenant</span>
                </div>
              </div>

              {/* Message d'exemple */}
              <div className="flex items-start gap-2">
                <img
                  src={SOPHIE_AVATAR}
                  alt="Sophie"
                  width={32}
                  height={32}
                  loading="lazy"
                  decoding="async"
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="bg-white rounded-lg p-3 shadow-soft text-sm">
                  <p className="text-neutral-700">
                    Vous pouvez me poser des questions sur nos programmes, nos services ou prendre rendez-vous.
                  </p>
                  <span className="text-xs text-neutral-500 mt-1 block">Sophie • maintenant</span>
                </div>
              </div>
            </div>
          </div>

          {/* Zone de saisie */}
          <div className="p-4 border-t border-neutral-200">
            <div className="flex gap-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tapez votre message..."
                className="flex-1 resize-none border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={2}
              />
              <button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className="bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white p-2 rounded-md transition-colors"
                aria-label="Envoyer le message"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            
            {/* Options rapides */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => setMessage('Bonjour, j\'aimerais en savoir plus sur vos programmes')}
                className="text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-2 py-1 rounded transition-colors"
              >
                Programmes
              </button>
              <button
                onClick={() => setMessage('Je souhaite prendre rendez-vous')}
                className="text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-2 py-1 rounded transition-colors"
              >
                Rendez-vous
              </button>
              <button
                onClick={() => setMessage('J\'ai une question sur un projet')}
                className="text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-2 py-1 rounded transition-colors"
              >
                Questions
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
